import {Component, computed, DestroyRef, inject, input, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {ColorRamp, ColorStop, MaterialNode, MaterialNodeSettings, ColorRampSockets} from "@cm/material-nodes"
import {MaterialGraphChangeService} from "@app/material-editor/services/material-graph-change.service"
import {PreviewSceneService} from "@app/material-editor/services/preview-scene.service"
import {MatSliderModule} from "@angular/material/slider"
import {MatButtonModule} from "@angular/material/button"
import {MatIconModule} from "@angular/material/icon"
import {FormsModule} from "@angular/forms"
import {CommonModule} from "@angular/common"
import {ScalarInputSocketComponent} from "@material-editor/components/sockets/scalar-input-socket/scalar-input-socket.component"
import {ColorInputSocketComponent} from "@material-editor/components/sockets/color-input-socket/color-input-socket.component"
import {ButtonComponent} from "@common/components/buttons/button/button.component"
import {asyncScheduler, BehaviorSubject, throttleTime} from "rxjs"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {linearToSrgb} from "@cm/image-processing/operations/tone-mapping"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-color-ramp-widget",
    templateUrl: "./color-ramp-widget.component.html",
    styleUrls: ["./color-ramp-widget.component.scss"],
    imports: [
        MatSliderModule,
        MatButtonModule,
        MatIconModule,
        FormsModule,
        CommonModule,
        ScalarInputSocketComponent,
        ColorInputSocketComponent,
        ButtonComponent,
    ],
})
export class ColorRampWidgetComponent implements OnInit {
    public readonly $material = input.required<MaterialNode>({alias: "material"})
    readonly graphChangeService = inject(MaterialGraphChangeService)
    readonly previewSceneService = inject(PreviewSceneService)
    readonly destroyRef = inject(DestroyRef)

    public readonly $colorRamp = computed(() => {
        const material = this.$material()
        if (!(material instanceof ColorRamp)) {
            throw new Error(`Expected a ColorRamp node, received ${this.$material()?.nodeClass}`)
        }
        return material
    })

    public readonly $interpolationMode = computed(() => {
        this.graphChangeService.$graphVersion() // Trigger recomputation when graph changes
        return this.$colorRamp().parameters.settings.colorRampInterpolation
    })

    public readonly $stopIndex = signal(0)
    public readonly $colorStops = signal<ColorStop[]>([])
    public readonly $stop = computed<ColorStop>(() => {
        return this.$colorStops()?.[this.$stopIndex()] ?? {position: 0, color: {r: 0, g: 0, b: 0}, alpha: 1}
    })
    public readonly $minPosition = computed(() => this.$colorStops()?.[this.$stopIndex() - 1]?.position ?? 0)
    public readonly $maxPosition = computed(() => this.$colorStops()?.[this.$stopIndex() + 1]?.position ?? 1)

    updatePreview$ = new BehaviorSubject<void>(undefined)

    ngOnInit() {
        this.$colorStops.set(this.$colorRamp().stops)

        this.updatePreview$
            .pipe(throttleTime(1000, asyncScheduler, {leading: true, trailing: true}), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.previewSceneService.$refresh.next())
    }

    selectStop(index: number) {
        this.$stopIndex.set(index)
    }

    incrementIndex() {
        this.$stopIndex.update((index) => {
            if (index === 0) {
                return 0
            }
            return index - 1
        })
    }

    decrementIndex() {
        this.$stopIndex.update((index) => {
            if (index === this.$colorStops().length - 1) {
                return index
            }
            return index + 1
        })
    }

    addColorStop() {
        this.$colorRamp().addStop(undefined, undefined)
        this.$colorStops.set(this.$colorRamp().stops)
        this.$stopIndex.set(this.$colorStops().length - 1)
        this.updatePreview$.next()
    }

    removeColorStop(index: number) {
        this.$colorRamp().removeStop(index)
        this.$colorStops.set(this.$colorRamp().stops)
        if (this.$stopIndex() >= this.$colorStops().length && this.$stopIndex() > 0) {
            this.$stopIndex.update((index) => index - 1)
        }
        this.updatePreview$.next()
    }

    updateColorStop(index: number, stop: Partial<ColorStop>) {
        this.$colorRamp().updateStop(index, stop, false)
        this.$colorStops.set(this.$colorRamp().stops)
        this.updatePreview$.next()
    }

    readonly $linearToSrgb = signal(linearToSrgb)

    readonly $rampImageDataUrl = computed(() => {
        const stops = this.$colorStops()
        if (stops.length < 2) return ""

        const settings: MaterialNodeSettings<ColorRampSockets> = {}
        stops.forEach((stop, index) => {
            settings[`colorRampElements_${index}Position`] = stop.position
            settings[`colorRampElements_${index}Color_0`] = stop.color.r
            settings[`colorRampElements_${index}Color_1`] = stop.color.g
            settings[`colorRampElements_${index}Color_2`] = stop.color.b
            settings[`colorRampElements_${index}Alpha`] = stop.alpha
        })
        settings.colorRampInterpolation = this.$interpolationMode()

        const {colorRampCyclesRampAlphaTable, colorRampCyclesRampColorTable} = ColorRamp.lookupTableFromStops(settings)
        const width = colorRampCyclesRampColorTable.length
        if (colorRampCyclesRampAlphaTable.length !== width) throw new Error("Color and Alpha tables have different lengths")
        const height = 1
        const imageData = new Uint8ClampedArray(width * height * 4)

        for (let x = 0; x < width; x++) {
            const color = colorRampCyclesRampColorTable[x]
            const alpha = colorRampCyclesRampAlphaTable[x]
            const offset = x * 4
            imageData[offset] = Math.round(linearToSrgb(color.r) * 255)
            imageData[offset + 1] = Math.round(linearToSrgb(color.g) * 255)
            imageData[offset + 2] = Math.round(linearToSrgb(color.b) * 255)
            imageData[offset + 3] = Math.round(alpha * 255)
        }

        const canvas = document.createElement("canvas")
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext("2d")
        if (!ctx) return ""
        const imgData = ctx.createImageData(width, height)
        imgData.data.set(imageData)
        ctx.putImageData(imgData, 0, 0)
        return canvas.toDataURL()
    })
}
